import { ref, onMounted, onUnmounted } from 'vue';
import { useNuxtApp } from '#app';

export function useLoading() {
  const { $gsap: gsap, $SmoothEase } = useNuxtApp();

  const progress = ref(0);
  const loadingProgress = useLoadingProgress(); // Stato globale
  const isLoading = ref(true);
  const isComplete = ref(false);

  const progressObj = { value: 0 };
  let progressTween = null;
  let simulationInterval = null;
  let onCompleteCallback = null;

  function onLoadingComplete(callback) {
    onCompleteCallback = callback;
  }

  function updateProgress(targetValue) {
    progressTween?.kill();

    progressTween = gsap.to(progressObj, {
      value: targetValue,
      duration: 1,
      ease: $SmoothEase,
      overwrite: true,
      onUpdate: () => {
        const roundedValue = Math.round(progressObj.value);
        loadingProgress.value = roundedValue;
        progress.value = roundedValue / 100;
      },
    });
  }

  async function loadAssets() {
    isLoading.value = true;
    isComplete.value = false;

    try {
      // Progresso simulato realistico che parte subito
      let simulatedProgress = 0;
      simulationInterval = setInterval(() => {
        // Rallenta man mano che si avvicina al 100
        const increment =
          simulatedProgress < 50 ? 3 : simulatedProgress < 80 ? 1.5 : 0.5;
        simulatedProgress = Math.min(simulatedProgress + increment, 90);
        updateProgress(simulatedProgress);
      }, 150);

      const images = document.images
        ? Array.from(document.images).map(img => {
            if (img.loading === 'lazy') {
              img.loading = 'eager';
            }
            return img;
          })
        : [];

      const videos = document.querySelectorAll
        ? Array.from(document.querySelectorAll('video'))
        : [];

      const total = images.length + videos.length;
      let loaded = 0;

      const handleAssetLoaded = () => {
        loaded++;
        // Aggiorna il progresso quando vengono caricate risorse reali
        const realProgress = total > 0 ? 90 + (loaded / total) * 10 : 95;
        simulatedProgress = Math.max(simulatedProgress, realProgress);
        updateProgress(simulatedProgress);
      };

      const imagePromises = images.map(img =>
        img.complete
          ? (handleAssetLoaded(), Promise.resolve())
          : new Promise(resolve => {
              img.addEventListener(
                'load',
                () => {
                  handleAssetLoaded();
                  resolve();
                },
                { once: true }
              );

              img.addEventListener(
                'error',
                () => {
                  handleAssetLoaded();
                  resolve();
                },
                { once: true }
              );
            })
      );

      const videoPromises = videos.map(video =>
        video.readyState >= 3
          ? (handleAssetLoaded(), Promise.resolve())
          : new Promise(resolve => {
              video.addEventListener(
                'canplaythrough',
                () => {
                  handleAssetLoaded();
                  resolve();
                },
                { once: true }
              );

              video.addEventListener(
                'error',
                () => {
                  handleAssetLoaded();
                  resolve();
                },
                { once: true }
              );

              if (video.preload !== 'auto') video.preload = 'auto';
              video.load?.();
            })
      );

      await Promise.all([...imagePromises, ...videoPromises]);

      clearInterval(simulationInterval);
      progressTween?.kill();
      updateProgress(100);

      await new Promise(resolve => {
        progressTween.eventCallback('onComplete', () => {
          isLoading.value = false;
          isComplete.value = true;
          onCompleteCallback?.();
          resolve();
        });
      });
    } catch (error) {
      updateProgress(100);
      isLoading.value = false;
      isComplete.value = true;
      onCompleteCallback?.();
    }
  }

  onMounted(async () => {
    await loadAssets();
  });

  onUnmounted(() => {
    if (simulationInterval) {
      clearInterval(simulationInterval);
      simulationInterval = null;
    }
    progressTween?.kill();
  });

  return {
    progress,
    loadingProgress,
    isLoading,
    isComplete,
    onLoadingComplete,
  };
}
